import React from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { Tooltip } from "./Tooltip";
import { utils } from "../Helpers";
import { Users, TrendingUp, Award, Target } from "lucide-react";

export const InfluencerTamaScore = ({
  userName,
  userData,
  totalFollowers,
  credibility,
  breakdown,
  tamaScore,
  tamaScoreText,
  performanceScore,
  performanceScoreText,
}) => {

  const followers = utils.convertNumber(totalFollowers?.IG[0]?.followers);

  const getBreakdownValue = (code) => {
    const item = breakdown?.find((item) => item.code === code);
    return item ? parseFloat((item.weight * 100).toFixed(1)) : 0;
  };

  const data = [
    {
      name: "Regular users",
      value: getBreakdownValue("real"),
      color: "#10b981",
    },
    {
      name: "Fake/bots followers",
      value: getBreakdownValue("suspicious"),
      color: "#ef4444",
    },
    {
      name: "Large number of following",
      value: getBreakdownValue("mass_followers"),
      color: "#f59e0b",
    },
    {
      name: "Influencers",
      value: getBreakdownValue("influencers"),
      color: "#3b82f6",
    },
  ];

  return (
    <div className="mx-auto px-4 sm:px-6 ">
      {/* Header */}
      {/* <div className="mb-6">
        <div className="flex items-center gap-3 ">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Influencer Insight
          </h1>
        </div>
      </div> */}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 ">
        {/* Left Column - Metrics */}
        <div className="lg:col-span-1 h-full space-y-6">
          {/* Followers Count */}
          {totalFollowers?.IG[0]?.followers > 0 && (
            <div className="bg-white rounded-2xl p-6 border border-green-100 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-green-100 rounded-lg mb-2">
                  <Users className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="text-3xl md:text-4xl font-bold text-gray-900">
                    {followers}
                  </div>
                  <p className="text-gray-500 text-sm">Followers</p>
                </div>
              </div>
            </div>
          )}
          {/* Followers Credibility */}
          {credibility > 0 && (
            <div className="bg-white rounded-2xl p-6 border border-green-100 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <div className="text-3xl md:text-4xl font-bold text-gray-900">
                    {(credibility * 100).toFixed(1)}%
                  </div>
                  <div className="flex items-center space-x-2">
                    <p className="text-gray-500 text-sm">
                      Followers Credibility
                    </p>
                    <Tooltip
                      description={
                        "To establish the credibility score for historical followers we take into account factors such as an account's avatar and bio description, number of posts, number of accounts followed vs following ratio. Influencers with a genuine audience will attain scores of 80 or above."
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Followers Breakdown */}
        {Array.isArray(data) && data.some((item) => item.value > 0) && (
          <div className="lg:col-span-2 col-span-2 row-span-2">
            <div className="bg-white rounded-2xl p-6 border border-green-100 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center gap-3 ">
                <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
                  <Award className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  Followers Breakdown
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Chart */}
                <div className="flex justify-center">
                  <div className="w-48 h-48 relative">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={data?.filter((item) => item.value > 0)}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {data
                            ?.filter((item) => item?.value > 0)
                            ?.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={entry?.color}
                                stroke="#fff"
                                strokeWidth={2}
                              />
                            ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Legend */}
                <div className="space-y-4">
                  {data?.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center space-x-3">
                        <div
                          className="w-4 h-4 rounded-full border-2 border-white shadow-sm"
                          style={{ backgroundColor: item.color }}
                        ></div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-700">
                            {item?.name}
                          </span>
                          {item?.name === "Large number of following" && (
                            <Tooltip
                              description={
                                "Accounts with more than 1500 following"
                              }
                            />
                          )}
                           {item?.name === "Influencers" && (
                            <Tooltip
                              description={
                                "Accounts with more than 5000 followers"
                              }
                            />
                          )}
                        </div>
                      </div>
                      <span className="text-sm font-bold text-gray-900 bg-white px-2 py-1 rounded-lg">
                        {item?.value ? `${item.value}%` : "coming soon"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* TAMA SCORE Section */}
      <div className="mb-8">
        <div className="bg-white rounded-2xl p-6 border border-green-100 shadow-lg hover:shadow-xl transition-all duration-300">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
              <Target className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">TAMA SCORE</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-0">
            {/* Left side - Pros & Cons */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-6">
                Performance Analysis
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Pros column */}
                <div className="space-y-4">
                  <div className="flex items-start space-x-3 p-3 bg-green-50 rounded-xl">
                    <div className="w-3 h-3 bg-green-500 rounded-full mt-1 flex-shrink-0"></div>
                    <span className="text-sm font-medium text-gray-700">
                      High audience reachability expected
                    </span>
                  </div>

                  <div className="flex items-start space-x-3 p-3 bg-yellow-50 rounded-xl">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full mt-1 flex-shrink-0"></div>
                    <span className="text-sm font-medium text-gray-700">
                      Average percentage of credible followers
                    </span>
                  </div>
                </div>

                {/* Cons column */}
                <div className="space-y-4">
                  <div className="flex items-start space-x-3 p-3 bg-red-50 rounded-xl">
                    <div className="w-3 h-3 bg-red-500 rounded-full mt-1 flex-shrink-0"></div>
                    <span className="text-sm font-medium text-gray-700">
                      This influencer is hiding likes
                    </span>
                  </div>

                  <div className="flex items-start space-x-3 p-3 bg-red-50 rounded-xl">
                    <div className="w-3 h-3 bg-red-500 rounded-full mt-1 flex-shrink-0"></div>
                    <span className="text-sm font-medium text-gray-700">
                      Poor followers growth rate
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right side - Score Cards in 2 columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Audience Quality Score */}
              <div className="bg-purple-100 rounded-lg border-2 border-purple-300 overflow-hidden">
                <div className="bg-purple-500 text-white text-center py-2">
                  <div className="flex items-center justify-center space-x-2">
                    <span className="text-xs font-medium">
                      AUDIENCE QUALITY SCORE
                    </span>
                    <div className="w-4 h-4 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                      <span className="text-xs">?</span>
                    </div>
                  </div>
                </div>
                <div className="p-6 text-center bg-white">
                  <div className="text-4xl font-bold text-purple-500 mb-1">
                    {tamaScore ? tamaScore : "51"}
                  </div>
                  <div className="text-sm text-purple-600 font-medium">
                    {tamaScoreText ? tamaScoreText : "Fare"}
                  </div>
                </div>
              </div>

              {/* Performance Score */}
              <div className="bg-emerald-100 rounded-lg border-2 border-emerald-300 overflow-hidden">
                <div className="bg-emerald-500 text-white text-center py-2">
                  <div className="flex items-center justify-center space-x-2">
                    <span className="text-xs font-medium">
                      PERFORMANCE SCORE
                    </span>
                    <div className="w-4 h-4 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                      <span className="text-xs">?</span>
                    </div>
                  </div>
                </div>
                <div className="p-6 text-center bg-white">
                  <div className="text-4xl font-bold text-emerald-500 mb-1">
                    {performanceScore ? performanceScore : "45"}
                  </div>
                  <div className="text-sm text-emerald-600 font-medium">
                    {performanceScoreText ? performanceScoreText : "Fair"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
